import { useState, useEffect, useCallback } from "react";
import { getStorageKey, removePageState } from "@/lib/utils/page-state";

export type ViewMode = "list" | "grid";

export interface PageState<T = Record<string, any>> {
  isInfoBannerVisible?: boolean;
  viewMode?: ViewMode;
  settings?: T;
}

/**
 * A hook to persist page state in localStorage.
 *
 * @param key - The unique key for the page/sub-page.
 * @param defaultState - The default state if nothing is found in localStorage.
 */
export function usePageState<T = Record<string, any>>(
  key: string,
  defaultState: PageState<T> = {}
) {
  const storageKey = getStorageKey(key);

  // Initialize state from localStorage or defaultState
  const [state, setState] = useState<PageState<T>>(() => {
    if (typeof window === 'undefined') return defaultState;
    try {
      const item = window.localStorage.getItem(storageKey);
      if (item) {
        return { ...defaultState, ...JSON.parse(item) };
      }
      return defaultState;
    } catch (error) {
      console.warn(`Error reading localStorage key "${storageKey}":`, error);
      return defaultState;
    }
  });

  // Persist state to localStorage whenever it changes
  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(state));
    } catch (error) {
      console.warn(`Error writing localStorage key "${storageKey}":`, error);
    }
  }, [storageKey, state]);

  // Setters
  const setInfoBannerVisible = useCallback((isVisible: boolean) => {
    setState((prev) => ({ ...prev, isInfoBannerVisible: isVisible }));
  }, []);

  const setViewMode = useCallback((mode: ViewMode) => {
    setState((prev) => ({ ...prev, viewMode: mode }));
  }, []);

  const setSettings = useCallback((newSettings: Partial<T> | ((prev: T | undefined) => Partial<T>)) => {
    setState((prev) => {
      const resolvedSettings =
        typeof newSettings === "function"
          ? newSettings(prev.settings)
          : newSettings;

      return {
        ...prev,
        settings: {
          ...prev.settings,
          ...resolvedSettings,
        } as T,
      };
    });
  }, []);

  // Generic setter for the whole state (use with caution)
  const setPageState = useCallback((newState: Partial<PageState<T>>) => {
    setState((prev) => ({ ...prev, ...newState }));
  }, []);

  const reset = useCallback(() => {
    setState(defaultState);
    removePageState(key);
  }, [defaultState, key]);

  return {
    state,
    setInfoBannerVisible,
    setViewMode,
    setSettings,
    setPageState,
    reset,
  };
}
